import { useMemo, useState } from 'react'
import {Zap, Bookmark, Trash2, Loader2, Search, X, Pencil} from 'lucide-react'
import type { RosterPattern } from './RosterCalculator'
import { useAuth } from '@/hooks/useAuth'
import { useRosterTemplates, type SavedTemplate } from '@/hooks/useRosterTemplates'
import TemplateEditDialog from '@/components/TemplateEditDialog'

interface TemplateSidebarProps {
  onSelectTemplate: (template: RosterPattern) => void
}

type CycleFilter = 'all' | 'single' | 'multi'

function describeCycles(cycles: Array<{ daysOn: number; daysOff: number }>): string {
  return cycles.map((c) => `${c.daysOn} on / ${c.daysOff} off`).join(', ')
}

export default function TemplateSidebar({ onSelectTemplate }: TemplateSidebarProps) {
  const { user, isAuthenticated } = useAuth()
  const {
    templates: savedTemplates,
    loading: savedLoading,
    deleteTemplate,
    updateTemplate
  } = useRosterTemplates(user?.userId)
  const [searchQuery, setSearchQuery] = useState('')
  const [cycleFilter, setCycleFilter] = useState<CycleFilter>('all')
  const [editingTemplate, setEditingTemplate] = useState<SavedTemplate | null>(null)
  const [templateToDelete, setTemplateToDelete] = useState<SavedTemplate | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState('')

  const confirmDelete = async () => {
    if (!templateToDelete) return
    setDeleting(true)
    setDeleteError('')
    try {
      await deleteTemplate(templateToDelete._id)
      setTemplateToDelete(null)
    } catch (error) {
      console.error('Failed to delete template:', error)
      setDeleteError('Could not delete this template. Please try again.')
    } finally {
      setDeleting(false)
    }
  }

  const filteredTemplates = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    return savedTemplates.filter((template) => {
      if (cycleFilter === 'single' && template.cycles.length !== 1) return false
      if (cycleFilter === 'multi' && template.cycles.length < 2) return false
      if (!query) return true
      const haystack = `${template.name} ${describeCycles(template.cycles)}`.toLowerCase()
      return haystack.includes(query)
    })
  }, [savedTemplates, searchQuery, cycleFilter])

  const isFiltering = searchQuery.trim().length > 0 || cycleFilter !== 'all'
  const templates: Array<RosterPattern & {name: string, popular: boolean, description: string}> = [
    // Simple Patterns
    { 
      name: "4 on, 4 off", 
      cycles: [{daysOn: 4, daysOff: 4}], 
      popular: true,
      description: "Standard 8-day cycle"
    },
    { 
      name: "7 on, 7 off", 
      cycles: [{daysOn: 7, daysOff: 7}], 
      popular: true,
      description: "Weekly rotation"
    },
    { 
      name: "9-5 Roster", 
      cycles: [{daysOn: 9, daysOff: 5}], 
      popular: true,
      description: "Extended work period"
    },
    { 
      name: "2 on, 2 off, 3 on, 2 off", 
      cycles: [{daysOn: 2, daysOff: 2}, {daysOn: 3, daysOff: 2}], 
      popular: true,
      description: "Pitman schedule"
    },
    
    // Complex Multi-Cycle Patterns
    { 
      name: "8-6-7-7 Roster", 
      cycles: [{daysOn: 8, daysOff: 6}, {daysOn: 7, daysOff: 7}], 
      popular: true,
      description: "FIFO complex rotation"
    },
    { 
      name: "8-6 Pattern", 
      cycles: [{daysOn: 8, daysOff: 6}], 
      popular: false,
      description: "14-day cycle"
    },
    { 
      name: "2 on, 2 off", 
      cycles: [{daysOn: 2, daysOff: 2}], 
      popular: false,
      description: "4-day cycle"
    },
    { 
      name: "14 on, 14 off", 
      cycles: [{daysOn: 14, daysOff: 14}], 
      popular: false,
      description: "Bi-weekly rotation"
    },
    { 
      name: "5 on, 2 off", 
      cycles: [{daysOn: 5, daysOff: 2}], 
      popular: false,
      description: "Standard work week"
    },
    { 
      name: "12 on, 12 off", 
      cycles: [{daysOn: 12, daysOff: 12}], 
      popular: false,
      description: "12-hour shifts"
    },
    { 
      name: "3 on, 3 off, 4 on, 4 off", 
      cycles: [{daysOn: 3, daysOff: 3}, {daysOn: 4, daysOff: 4}], 
      popular: false,
      description: "DuPont rotation"
    },
    { 
      name: "10 on, 4 off", 
      cycles: [{daysOn: 10, daysOff: 4}], 
      popular: false,
      description: "Mining industry standard"
    },
  ]

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 sticky top-4">
      <div className="flex items-center gap-2 mb-4">
        <Zap className="w-6 h-6 text-orange-500" />
        <h3 className="text-lg font-bold text-slate-900">Quick Templates</h3>
      </div>
      
      <p className="text-sm text-slate-600 mb-4">
        Popular roster patterns for instant calculation
      </p>

      <div className="space-y-2">
        {templates.map((template, index) => (
          <button
            key={index}
            onClick={() => onSelectTemplate(template)}
            className="w-full text-left px-4 py-3 rounded-lg border border-slate-200 hover:border-teal-400 hover:bg-teal-50 transition-all duration-200 group"
          >
            <div className="flex justify-between items-center">
              <span className="font-medium text-slate-900 group-hover:text-teal-700">
                {template.name}
              </span>
              {template.popular && (
                <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full font-semibold">
                  Popular
                </span>
              )}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {template.description}
            </div>
          </button>
        ))}
      </div>

      {isAuthenticated && (
        <div className="mt-6 pt-6 border-t border-slate-200">
          <div className="flex items-center gap-2 mb-3">
            <Bookmark className="w-5 h-5 text-teal-500" />
            <h3 className="text-lg font-bold text-slate-900">My Saved Templates</h3>
          </div>

          {savedTemplates.length > 0 && !savedLoading && (
            <div className="mb-3 space-y-2">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search my templates"
                  aria-label="Search my saved templates by name or pattern"
                  className="w-full pl-9 pr-9 py-2 text-sm border border-slate-300 rounded-lg text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 rounded transition-colors"
                    aria-label="Clear search"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              <div className="flex gap-1.5" role="group" aria-label="Filter saved templates by cycle type">
                {([
                  { value: 'all', label: 'All' },
                  { value: 'single', label: 'Single cycle' },
                  { value: 'multi', label: 'Multi cycle' },
                ] as Array<{ value: CycleFilter; label: string }>).map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setCycleFilter(option.value)}
                    aria-pressed={cycleFilter === option.value}
                    className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-colors ${
                      cycleFilter === option.value
                        ? 'bg-teal-600 text-white border-teal-600'
                        : 'bg-white text-slate-600 border-slate-300 hover:border-teal-400 hover:text-teal-700'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {savedLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
            </div>
          ) : savedTemplates.length === 0 ? (
            <p className="text-xs text-slate-500">
              No saved templates yet. Build a custom pattern and save it for quick access.
            </p>
          ) : filteredTemplates.length === 0 ? (
            <div>
              <p className="text-xs text-slate-600 mb-2">
                No saved templates match your search.
              </p>
              <button
                onClick={() => {
                  setSearchQuery('')
                  setCycleFilter('all')
                }}
                className="text-xs font-semibold text-teal-700 underline hover:text-teal-900"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredTemplates.map((template) => (
                <div
                  key={template._id}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 hover:border-teal-400 hover:bg-teal-50 transition-all group"
                >
                  <button
                    onClick={() => onSelectTemplate({ name: template.name, cycles: template.cycles })}
                    className="flex-1 text-left min-w-0"
                  >
                    <span className="font-medium text-sm text-slate-900 group-hover:text-teal-700 truncate block">
                      {template.name}
                    </span>
                    <span className="text-xs text-slate-500 truncate block">
                      {describeCycles(template.cycles)}
                    </span>
                  </button>
                  <button
                    onClick={() => setEditingTemplate(template)}
                    className="p-1.5 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors flex-shrink-0"
                    aria-label={`Rename or edit template ${template.name}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      setDeleteError('')
                      setTemplateToDelete(template)
                    }}
                    className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                    aria-label={`Delete template ${template.name}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {isFiltering && filteredTemplates.length > 0 && (
            <p className="text-xs text-slate-500 mt-2" aria-live="polite">
              Showing {filteredTemplates.length} of {savedTemplates.length} saved templates
            </p>
          )}
        </div>
      )}

      <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
        <p className="text-xs text-slate-600 leading-relaxed">
          <span className="font-semibold">Pro Tip:</span> For complex patterns like 8-6-7-7, 
          use our custom calculator above to alternate between different cycles.
        </p>
      </div>

      <TemplateEditDialog
        template={editingTemplate}
        onClose={() => setEditingTemplate(null)}
        onSave={updateTemplate}
      />

      {templateToDelete && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-template-title"
        >
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6">
            <div className="flex items-start gap-3 mb-3">
              <div className="p-2 bg-red-50 rounded-lg flex-shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 id="delete-template-title" className="text-lg font-bold text-slate-900">
                  Delete this template?
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  &ldquo;{templateToDelete.name}&rdquo; will be removed from your saved templates. This cannot
                  be undone.
                </p>
              </div>
            </div>

            {deleteError && (
              <p className="mb-3 p-2.5 text-sm bg-red-50 text-red-800 border border-red-200 rounded-lg">
                {deleteError}
              </p>
            )}

            <div className="flex gap-3 mt-5">
              <button
                onClick={() => setTemplateToDelete(null)}
                className="flex-1 px-4 py-2.5 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium text-sm"
                autoFocus
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-sm disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
